import React, { useContext, useState } from 'react';
import { Alert, Box, Button, FormField, Input, Select, SpaceBetween } from '@amzn/awsui-components-react/polaris';
import CONSTANTS from 'src/utils/constants';
import { ColumnDataType, PeriodBehavior } from 'src/models/segment-cdt/SegmentCdtTypes';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import { ACTIONS } from 'src/services/segment-cdt/SegmentTablesReducer';
import { buildColumnDef, PERIOD_BEHAVIOR_OPTIONS } from 'src/components/segment-cdt/create/segmentCreateUtils';
import CurrencyAutosuggest from 'src/components/segment-cdt/create/CurrencyAutosuggest';

const DATA_TYPE_OPTIONS = [
    { value: ColumnDataType.NUMBER, label: 'Number' },
    { value: ColumnDataType.PERCENTAGE, label: 'Percentage' },
    { value: ColumnDataType.TEXT, label: 'Text' },
];

/**
 * "Manual Upload" column config: a blank column filled later via Upload. Captures displayName +
 * dataType, plus a currency (required, per the contract) only when the data type is NUMBER.
 */
export default function ManualColumnForm({ hideCancel = false }: { hideCancel?: boolean }) {
    const { state, dispatch, services } = useContext(SegmentTablesContext);
    const cw = state.createWizard;
    const editor = cw.columnEditor;
    const draft = editor?.draft ?? {};
    const [localError, setLocalError] = useState('');
    // Pass a stable non-empty value so the currency codes load once (the LOV ignores the query and
    // returns the full list); the Autosuggest filters them client-side. See GlPullColumnForm.
    const [currencyOptions] = services.customDataTablesService.getCurrencyCodeLOV('load');

    const setDraft = (patch: any) => dispatch(ACTIONS.SET_COLUMN_DRAFT.withPayload(patch));

    const onAdd = () => {
        // Duplicate checks span the union of the table's existing columns (empty in the create wizard,
        // populated when adding columns to an existing table) and the queued columns.
        const union = [...(state.detail.definition?.columns ?? []), ...cw.columns];
        const others = union.map((c) => c.displayName || '');
        const otherIds = union.map((c) => c.columnId).filter((id): id is string => !!id);
        // Only enforce currency-membership once the LOV has loaded; an empty list means "cannot check".
        const validCurrencies = (currencyOptions ?? []).length ? [CONSTANTS.STAT_CURRENCY, ...(currencyOptions ?? [])] : [];
        const { column, error } = buildColumnDef(draft, others, otherIds, validCurrencies, union);
        if (error || !column) {
            setLocalError(error || 'Invalid column.');
            return;
        }
        dispatch(ACTIONS.COMMIT_COLUMN.withPayload(column));
    };

    const isNumber = draft.dataType === ColumnDataType.NUMBER;

    return (
        <SpaceBetween size="m">
            <FormField label="Column name" description="Enter a unique name for this column.">
                <Input
                    value={draft.displayName ?? ''}
                    placeholder="e.g. Adjustments"
                    onChange={({ detail }) => setDraft({ displayName: detail.value })}
                />
            </FormField>
            <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                <img src={CONSTANTS.IMAGE_PATH + 'segment-manual-upload.svg'} width={28} height={28} alt="" style={{ display: 'block' }} />
                <Box variant="h4" padding={{ top: 'n', bottom: 'n' }}>Configure column: Manual Upload</Box>
            </SpaceBetween>
            <FormField label="Data type">
                <Select
                    selectedOption={DATA_TYPE_OPTIONS.find((o) => o.value === draft.dataType) ?? null}
                    options={DATA_TYPE_OPTIONS}
                    placeholder="Select a data type"
                    onChange={({ detail }) => setDraft({ dataType: detail.selectedOption.value as ColumnDataType })}
                />
            </FormField>
            {isNumber && (
                <FormField label="Currency" description="Required for a Number column (e.g. STAT).">
                    <CurrencyAutosuggest
                        options={currencyOptions ?? []}
                        value={draft.currency ?? ''}
                        onChange={(v) => setDraft({ currency: v })}
                    />
                </FormField>
            )}
            <FormField
                label="Period behaviour"
                description="What happens to entered values when a new period opens."
            >
                <Select
                    selectedOption={PERIOD_BEHAVIOR_OPTIONS.find((o) => o.value === draft.periodBehavior) ?? null}
                    // "Not set" is offered only once a behaviour has been chosen, so it reads as clearing a
                    // choice rather than as a third behaviour to pick from.
                    options={draft.periodBehavior
                        ? [{ value: '', label: 'Not set' }, ...PERIOD_BEHAVIOR_OPTIONS]
                        : PERIOD_BEHAVIOR_OPTIONS}
                    placeholder="Optional - not set"
                    onChange={({ detail }) => setDraft({
                        periodBehavior: (detail.selectedOption.value || undefined) as PeriodBehavior | undefined,
                    })}
                />
            </FormField>
            <Alert type="info">The column is added blank. Fill its values later via Upload. There is no inline editing here.</Alert>
            {localError && <Alert type="error">{localError}</Alert>}
            <Box float="right">
                <SpaceBetween size="xs" direction="horizontal">
                    {!hideCancel && <Button onClick={() => dispatch(ACTIONS.CANCEL_COLUMN_EDITOR)}>Cancel</Button>}
                    <Button variant="primary" onClick={onAdd}>
                        Add empty column
                    </Button>
                </SpaceBetween>
            </Box>
        </SpaceBetween>
    );
}
